import { ChangeEvent, Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { ExpandMore } from "@mui/icons-material";
import { Chip, Grid2, IconButton, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_RowSelectionState,
  useMaterialReactTable
} from "material-react-table";
import {
  DetailedServiceBookingData,
  DetailedServiceCatering
} from "../Domain/RemoteOrderRequests.types";
import useServiceTypeColor from "../hooks/useServiceTypeColor";
import { OrderRequestCateringRow } from "../Domain/OrderRequestList.types";
import { renderCateringPricePerUnitCell } from "../../Catering/catering-list.partial";
import {
  renderServiceBookingLeadTime,
  renderServiceImage,
  renderServiceSetupTime
} from "../../../FacilityManager/Components/Toolbars/InfoSidebar/ZoneInfoSidebar/ServicePartial/ServiceCommonRender.partial";
import { commonLocalization } from "../../../../functions/tableLocalization";
import { handleInitialItemSelect } from "../functions/handleInitialItemSelect.function";
import {
  ServiceDeliveryStatus,
  ServiceType
} from "../../../../features/Booking-Form/typings/booking-service-administration.types";
import { DetailDataChanges } from "./OrderRequestDetailDialog.partial";
import { BookingRequestsStatus } from "../../../BookingRequest/typings/booking-requests.types";

import { EnlargedImageDialog } from "../../../../features/EnlargedImageCarousel/enlarged-image-dialog.component";
import { resolveResourceCateringOrderedQty } from "../functions/normalizeOrderRequestDetailData.function";
import { qtyHelperText } from "../functions/qtyHelperText.function";
import { statusEditSelectOpts } from "./OrderRequestCardItem.partial";
import { renderServiceLineStatusLabelCell } from "./OrderRequestDetailServiceLineStatusCell.partial";
import { withSeeditMrtInitialState } from "../../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../../themes/use-seedit-mrt-table-options";

type Props = {
  listOpen: boolean;
  setListOpen: (value: SetStateAction<{ [key: number]: boolean }>) => void;
  detailData: DetailedServiceBookingData;
  editedData: DetailedServiceBookingData | null;
  handleDetailData: (changes: DetailDataChanges) => void;
  cateringRowSelection: MRT_RowSelectionState;
  setCateringRowSelection: Dispatch<SetStateAction<MRT_RowSelectionState>>;
  isArchive: boolean;
  isBookingCancelled: boolean;
  isPostLoading: boolean;
  chipStyle: any;
  cateringQuantityErrors: Record<number, "min" | "max" | "empty" | null>;
  /** Service admin for the booking location can edit quantities when order progress is READY. */
  isServiceAdminBypass?: boolean;
  /** Standalone resource catering parent — ordered quantity is editable. */
  isResourceCateringOrder?: boolean;
};

export function OrderRequestDetailCateringTable({
  listOpen,
  setListOpen,
  detailData,
  editedData,
  handleDetailData,
  cateringRowSelection,
  setCateringRowSelection,
  isArchive,
  isBookingCancelled,
  isPostLoading,
  chipStyle,
  cateringQuantityErrors,
  isServiceAdminBypass = false,
  isResourceCateringOrder = false
}: Readonly<Props>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { cateringColor } = useServiceTypeColor();

  const [cateringData, setCateringData] = useState<OrderRequestCateringRow[]>([]);
  const [showBiggerImage, setShowBiggerImage] = useState<string | null>(null);

  const handleChangeQuantity = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    row: MRT_Row<DetailedServiceCatering>
  ) => {
    handleDetailData({
      type: ServiceType.Catering,
      id: row.original.id,
      originalQty: row.original.quantity,
      newQty: Number(event.target.value),
      status: row.original.status
    });
  };

  const handleChangeOrderedQuantity = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    row: MRT_Row<DetailedServiceCatering>
  ) => {
    handleDetailData({
      type: ServiceType.Catering,
      id: row.original.id,
      originalQty: row.original.orderedQty ?? row.original.quantity,
      newQty: Number(event.target.value),
      status: row.original.status
    });
  };

  const handleChangeStatus = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    row: MRT_Row<DetailedServiceCatering>
  ) => {
    handleDetailData({
      type: ServiceType.Catering,
      id: row.original.id,
      originalQty: row.original.quantity,
      newQty: row.original.quantity,
      status: event.target.value as BookingRequestsStatus
    });
  };

  useEffect(() => {
    if (!editedData) return;

    setCateringData(editedData.caterings);
    handleInitialItemSelect(
      isArchive,
      isBookingCancelled,
      editedData.caterings,
      setCateringRowSelection
    );
  }, [isArchive, isBookingCancelled, editedData?.caterings]);

  const showDeliveryQty = (row: MRT_Row<DetailedServiceCatering>) => (
    <Typography>{Math.min(row.original.availableQty ?? 0, row.original.orderedQty)}</Typography>
  );

  const deliveryLockedForEditing =
    editedData?.deliveryStatus === ServiceDeliveryStatus.READY && !isServiceAdminBypass;

  const cateringChipCount = (() => {
    if (isResourceCateringOrder) {
      return resolveResourceCateringOrderedQty(detailData);
    }
    if (detailData.numberOfCaterings > 0) {
      return detailData.numberOfCaterings;
    }
    return detailData.caterings?.length ?? 1;
  })();

  const conferenceQuantityColumns = useMemo<MRT_ColumnDef<OrderRequestCateringRow>[]>(
    () => [
      {
        accessorKey: "quantity",
        header: t("Delivery Qty"),
        enableEditing: false,
        Cell: ({ row }) => showDeliveryQty(row),
        size: 100
      },
      {
        accessorKey: "orderedQty",
        header: t("Ordered Qty"),
        enableEditing: false,
        size: 100
      },
      {
        accessorKey: "availableQty",
        header: t("Available Qty"),
        enableEditing: true,
        size: 100,
        muiEditTextFieldProps: ({ row }) => {
          const hasError = !!cateringQuantityErrors[row.original.id];
          const isOverMax =
            row.original.orderedQty >
            (editedData?.caterings.find(cat => cat.id === row.original.id)?.orderedQty ?? 5);
          const isStayNull = row.original.availableQty === null;
          const disabledToEdit = deliveryLockedForEditing;

          return {
            type: "number",
            onChange: event => {
              row.original.availableQty = Number(event.target.value);
              handleChangeQuantity(event, row);
            },
            inputProps: {
              step: 1,
              min: 1,
              max: editedData?.caterings.find(cat => cat.id === row.original.id)?.orderedQty ?? 5,
              disabled: disabledToEdit
            },
            sx: { width: "150px" },
            error: hasError || isOverMax || isStayNull,
            helperText: qtyHelperText(row, cateringQuantityErrors)
          };
        }
      }
    ],
    [cateringQuantityErrors, deliveryLockedForEditing, editedData?.caterings, t]
  );

  const resourceQuantityColumn = useMemo<MRT_ColumnDef<OrderRequestCateringRow>>(
    () => ({
      accessorKey: "orderedQty",
      header: t("Qty"),
      enableEditing: true,
      size: 100,
      muiEditTextFieldProps: ({ row }) => {
        const hasError = !!cateringQuantityErrors[row.original.id];

        return {
          type: "number",
          onChange: event => {
            row.original.orderedQty = Number(event.target.value);
            handleChangeOrderedQuantity(event, row);
          },
          inputProps: {
            step: 1,
            min: 1,
            disabled: deliveryLockedForEditing
          },
          sx: { width: "150px" },
          error: hasError,
          helperText: qtyHelperText(row, cateringQuantityErrors)
        };
      }
    }),
    [cateringQuantityErrors, deliveryLockedForEditing, t]
  );

  const column = useMemo<MRT_ColumnDef<OrderRequestCateringRow>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("Name"),
        enableEditing: false,
        size: 200
      },
      ...(isResourceCateringOrder ? [resourceQuantityColumn] : conferenceQuantityColumns),
      {
        accessorKey: "status",
        header: t("Status"),
        Cell: ({ cell }) => renderServiceLineStatusLabelCell(cell.row.original.status, t),
        editSelectOptions: () => statusEditSelectOpts(),
        muiEditTextFieldProps: ({ row }) => {
          return {
            onChange: event => handleChangeStatus(event, row)
          };
        },
        editVariant: "select",
        enableEditing: row => row.original.status !== BookingRequestsStatus.CANCELLED,
        size: 100
      },
      {
        size: 250,
        enableEditing: false,
        accessorKey: "description",
        header: t("Description")
      },
      {
        header: t("Price/Unit"),
        accessorKey: "pricePerUnit",
        enableEditing: false,
        size: 50,
        Cell: ({ row: { original } }) => renderCateringPricePerUnitCell(original.pricePerUnit)
      },
      {
        header: t("Booking Lead Time"),
        Header: renderServiceBookingLeadTime(),
        size: 50,
        enableEditing: false,
        accessorKey: "bookingLeadTime"
      },
      {
        header: t("Setup Time"),
        Header: renderServiceSetupTime(),
        enableEditing: false,
        size: 50,
        accessorKey: "setupTime"
      },
      {
        accessorKey: "image",
        header: t("Image"),
        Cell: ({ cell }) => renderServiceImage(100, cell.row.original.image, setShowBiggerImage),
        size: 100,
        enableEditing: false,
        enableColumnFilter: false,
        enableSorting: false
      }
    ],
    [
      cateringData,
      cateringQuantityErrors,
      conferenceQuantityColumns,
      editedData,
      isResourceCateringOrder,
      isServiceAdminBypass,
      resourceQuantityColumn,
      t
    ]
  );

  const seeditMrtOptions = useSeeditMrtTableOptions<OrderRequestCateringRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns: column,
    data: editedData?.caterings ?? [],
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 }
    }),
    state: {
      rowSelection: cateringRowSelection,
      isSaving: isPostLoading
    },
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("There are no catering")
    },
    editDisplayMode: "table",
    getRowId: row => row.id?.toString(),
    onRowSelectionChange: setCateringRowSelection,
    positionToolbarAlertBanner: "none",
    paginationDisplayMode: "pages",
    muiPaginationProps: { rowsPerPageOptions: [5, 10, 15, 20, 30], variant: "outlined" },
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableRowSelection: false,
    autoResetPageIndex: false,
    enableEditing: !isArchive && !isBookingCancelled && !deliveryLockedForEditing
  });

  return (
    <>
      <Grid2 container sx={{ width: "100%", flexDirection: "column" }}>
        <Grid2>
          {/* header of catering table */}
          <IconButton
            data-testid={"expandButton"}
            sx={[
              {
                transition: theme.transitions.create("transform", {
                  duration: theme.transitions.duration.shortest
                })
              },
              listOpen ? { transform: "rotate(0deg)" } : { transform: "rotate(-90deg)" }
            ]}
            onClick={() => setListOpen(prev => ({ ...prev, [0]: !prev[0] }))}
          >
            <ExpandMore />
          </IconButton>

          <Chip
            label={t("OrderRequest_Catering", {
              number: cateringChipCount
            })}
            sx={{ ...chipStyle, backgroundColor: cateringColor }}
          />
        </Grid2>

        {/* catering table */}
        <Grid2 sx={{ pl: 5 }}>
          <MaterialReactTable table={table} />
        </Grid2>
      </Grid2>
      <EnlargedImageDialog
        open={!!showBiggerImage}
        picturePath={showBiggerImage}
        handleClose={() => setShowBiggerImage(null)}
      />
    </>
  );
}
